import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { DataTable, ExportButton, Breadcrumbs, BreadcrumbItem } from '../components/ui';
import type { Column } from '../components/ui/DataTable';
import type { ApiLog } from '../types';
import {
  Activity,
  Clock,
  Filter,
  Server,
  DollarSign,
  Eye,
  X,
  Copy,
  Check,
  Terminal,
  Code2,
  Cpu,
  ExternalLink,
  ShieldCheck,
  Layers,
} from 'lucide-react';

export const ApiLogs: React.FC = () => {
  const [filters, setFilters] = useState({
    client_id: '',
    endpoint: '',
    status_code: '',
    environment: '',
    date_from: '',
    date_to: '',
  });

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  // Selected Log for Deep Inspection Drawer/Modal
  const [selectedLog, setSelectedLog] = useState<ApiLog | null>(null);
  const [activeTab, setActiveTab] = useState<'payloads' | 'headers' | 'replay'>('payloads');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setOffset(0);
  }, [filters, limit]);

  // Fetch clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients-list'],
    queryFn: async () => {
      const response = await api.get<{ data: any[] }>('/admin/clients?limit=1000');
      return response.data.data;
    },
  });

  const { data, isLoading } = useQuery<{ data: ApiLog[]; pagination: { total: number } }>({
    queryKey: ['api-logs', filters, offset, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{ data: ApiLog[]; pagination: { total: number } }>(
        `/admin/api-logs?${params.toString()}`
      );
      return response.data;
    },
  });

  const logs = data?.data || [];
  const total = data?.pagination?.total || 0;

  const getMethodBadge = (method: string) => {
    const m = (method || '').toUpperCase();
    switch (m) {
      case 'GET':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50';
      case 'POST':
        return 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800/50';
      case 'PUT':
      case 'PATCH':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50';
      case 'DELETE':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  };

  const getStatusBadge = (code: number) => {
    if (code >= 200 && code < 300)
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50';
    if (code >= 300 && code < 400)
      return 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800/50';
    if (code >= 400 && code < 500)
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50';
    if (code >= 500)
      return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50';
    return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  };

  const getStatusLabel = (code: number) => {
    switch (code) {
      case 200:
        return '200 OK';
      case 201:
        return '201 Created';
      case 204:
        return '204 No Content';
      case 400:
        return '400 Bad Request';
      case 401:
        return '401 Unauthorized';
      case 403:
        return '403 Forbidden';
      case 404:
        return '404 Not Found';
      case 429:
        return '429 Rate Limit Exceeded';
      case 500:
        return '500 Internal Error';
      case 502:
        return '502 Bad Gateway';
      case 503:
        return '503 Service Unavailable';
      case 504:
        return '504 Gateway Timeout';
      default:
        return `${code} Status`;
    }
  };

  const generateCurl = (log: ApiLog) => {
    const baseUrl = 'https://api-gateway.tatusolutions.com';
    const cleanEndpoint = log.endpoint.startsWith('/') ? log.endpoint : `/${log.endpoint}`;
    const method = (log.http_method || 'GET').toUpperCase();

    let cmd = `curl -X ${method} "${baseUrl}${cleanEndpoint}" \\\n`;
    cmd += `  -H "Content-Type: application/json" \\\n`;
    cmd += `  -H "X-API-Key: <YOUR_API_KEY>"`;

    if (log.request_payload && Object.keys(log.request_payload).length > 0 && method !== 'GET') {
      cmd += ` \\\n  -d '${JSON.stringify(log.request_payload, null, 2)}'`;
    }

    return cmd;
  };

  const columns: Column<ApiLog>[] = [
    {
      key: 'created_at',
      header: 'Data/Hora',
      render: (log) => (
        <span className="text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
          {new Date(log.created_at).toLocaleString('pt-BR')}
        </span>
      ),
    },
    {
      key: 'client',
      header: 'Cliente',
      render: (log) =>
        log.client ? (
          <Link
            to={`/clients/${log.client_id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs font-semibold"
          >
            {log.client.company_name}
          </Link>
        ) : (
          <span className="text-slate-400 text-xs">Desconhecido</span>
        ),
    },
    {
      key: 'endpoint',
      header: 'Endpoint',
      render: (log) => (
        <span
          className="font-mono text-xs text-slate-800 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700/60 max-w-xs truncate block"
          title={log.endpoint}
        >
          {log.endpoint}
        </span>
      ),
    },
    {
      key: 'http_method',
      header: 'Método',
      render: (log) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-2xs font-bold font-mono border ${getMethodBadge(
            log.http_method
          )}`}
        >
          {log.http_method}
        </span>
      ),
    },
    {
      key: 'status_code',
      header: 'Estado',
      render: (log) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold font-mono border ${getStatusBadge(
            log.status_code
          )}`}
        >
          {log.status_code}
        </span>
      ),
    },
    {
      key: 'response_time_ms',
      header: 'Latência',
      render: (log) => (
        <span className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1">
          <Clock className="w-3 h-3 text-slate-400" />
          <span>{log.response_time_ms}ms</span>
        </span>
      ),
    },
    {
      key: 'cost',
      header: 'Custo',
      render: (log) => (
        <span className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-0.5 font-mono">
          <DollarSign className="w-3 h-3 text-slate-400" />
          <span>{Number(log.cost).toFixed(4)}</span>
        </span>
      ),
    },
    {
      key: 'environment',
      header: 'Ambiente',
      render: (log) => (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold border capitalize ${
            log.environment === 'production'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50'
              : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50'
          }`}
        >
          <Server className="w-2.5 h-2.5" />
          <span>{log.environment === 'production' ? 'Produção' : 'Sandbox'}</span>
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (log) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLog(log);
          }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 bg-primary-50 dark:bg-primary-950/50 hover:bg-primary-100 dark:hover:bg-primary-900/60 rounded-lg border border-primary-200/60 dark:border-primary-800/60 transition-colors"
          title="Inspecionar requisição e payloads"
        >
          <Eye className="w-3.5 h-3.5" />
          <span>Trace</span>
        </button>
      ),
    },
  ];

  const exportHeaders = [
    { key: 'created_at', label: 'Data/Hora' },
    { key: 'client_name', label: 'Cliente' },
    { key: 'endpoint', label: 'Endpoint' },
    { key: 'http_method', label: 'Método' },
    { key: 'status_code', label: 'Estado' },
    { key: 'response_time_ms', label: 'Tempo Resposta (ms)' },
    { key: 'cost', label: 'Custo' },
    { key: 'environment', label: 'Ambiente' },
  ];

  const exportData = logs.map((log) => ({
    ...log,
    client_name: log.client?.company_name || 'Desconhecido',
  }));

  // Latency breakdown calculations for Kong Konnect conformity
  const gatewayOverhead = selectedLog
    ? Math.max(2, Math.round(selectedLog.response_time_ms * 0.08))
    : 0;
  const upstreamLatency = selectedLog
    ? Math.max(1, selectedLog.response_time_ms - gatewayOverhead)
    : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Logs de API</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Activity className="w-8 h-8 text-primary-500" />
            <span>Logs de Tráfego de API</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Inspecione em tempo real chamadas, latência, custos e códigos de resposta do Gateway
          </p>
        </div>
        <ExportButton
          data={exportData}
          filename={`api_logs_${Date.now()}.csv`}
          headers={exportHeaders}
        />
      </div>

      {/* Filter Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold text-sm">
          <Filter className="w-4 h-4 text-primary-500" />
          <span>Filtros de Pesquisa</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.client_id}
            onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
          >
            <option value="">Todos os Clientes</option>
            {clientsData?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Filtrar por endpoint (ex: /v1/users)..."
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden placeholder-slate-400"
            value={filters.endpoint}
            onChange={(e) => setFilters({ ...filters, endpoint: e.target.value })}
          />

          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.status_code}
            onChange={(e) => setFilters({ ...filters, status_code: e.target.value })}
          >
            <option value="">Todos os status HTTP</option>
            <option value="200">200 OK</option>
            <option value="400">400 Bad Request</option>
            <option value="401">401 Unauthorized</option>
            <option value="403">403 Forbidden</option>
            <option value="404">404 Not Found</option>
            <option value="429">429 Too Many Requests</option>
            <option value="500">500 Internal Server Error</option>
          </select>

          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.environment}
            onChange={(e) => setFilters({ ...filters, environment: e.target.value })}
          >
            <option value="">Todos os Ambientes</option>
            <option value="sandbox">Sandbox (Testes)</option>
            <option value="production">Produção</option>
          </select>

          <div className="flex items-center gap-2">
            <span className="text-2xs font-semibold text-slate-400 dark:text-slate-500 uppercase">
              De:
            </span>
            <input
              type="date"
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-2xs font-semibold text-slate-400 dark:text-slate-500 uppercase">
              Até:
            </span>
            <input
              type="date"
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            />
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={logs}
        isLoading={isLoading}
        total={total}
        limit={limit}
        offset={offset}
        onPageChange={setOffset}
        onLimitChange={setLimit}
        onRowClick={(log) => setSelectedLog(log)}
        emptyTitle="Nenhum log de API encontrado"
        emptyDescription="Tente ajustar os seus filtros de pesquisa para visualizar outros registros."
      />

      {/* Deep Request Trace & Payload Modal */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setSelectedLog(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/70 dark:bg-slate-800/40">
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold font-mono border ${getMethodBadge(
                    selectedLog.http_method
                  )}`}
                >
                  {selectedLog.http_method}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold font-mono border ${getStatusBadge(
                    selectedLog.status_code
                  )}`}
                >
                  {getStatusLabel(selectedLog.status_code)}
                </span>
                <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-200 truncate max-w-md">
                  {selectedLog.endpoint}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${
                    selectedLog.environment === 'production'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50'
                      : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50'
                  }`}
                >
                  <Server className="w-3 h-3" />
                  <span>{selectedLog.environment === 'production' ? 'Produção' : 'Sandbox'}</span>
                </span>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  aria-label="Fechar modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Quick Metrics & Metadata Grid */}
            <div className="px-6 py-3.5 bg-slate-100/60 dark:bg-slate-950/40 border-b border-slate-200 dark:border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-slate-400 dark:text-slate-500 font-medium block">
                  Data & Hora
                </span>
                <span className="text-slate-800 dark:text-slate-200 font-semibold font-mono">
                  {new Date(selectedLog.created_at).toLocaleString('pt-BR')}
                </span>
              </div>

              <div>
                <span className="text-slate-400 dark:text-slate-500 font-medium block">
                  Cliente
                </span>
                {selectedLog.client ? (
                  <Link
                    to={`/clients/${selectedLog.client_id}`}
                    className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
                  >
                    {selectedLog.client.company_name}
                  </Link>
                ) : (
                  <span className="text-slate-700 dark:text-slate-300 font-semibold">
                    Desconhecido
                  </span>
                )}
              </div>

              <div>
                <span className="text-slate-400 dark:text-slate-500 font-medium block">
                  Latência Total
                </span>
                <div className="flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-200 font-mono">
                  <Clock className="w-3.5 h-3.5 text-primary-500" />
                  <span>{selectedLog.response_time_ms} ms</span>
                  <span className="text-2xs text-slate-400 font-normal">
                    (Gateway: {gatewayOverhead}ms / Upstream: {upstreamLatency}ms)
                  </span>
                </div>
              </div>

              <div>
                <span className="text-slate-400 dark:text-slate-500 font-medium block">
                  Custo / Log ID
                </span>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">
                    ${Number(selectedLog.cost).toFixed(4)}
                  </span>
                  <button
                    onClick={() => copyToClipboard(selectedLog.id, 'id')}
                    className="inline-flex items-center gap-1 text-2xs text-slate-400 hover:text-primary-500 font-mono transition-colors"
                    title="Copiar ID do Log"
                  >
                    {copiedKey === 'id' ? (
                      <Check className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                    <span>{selectedLog.id.slice(0, 8)}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex border-b border-slate-200 dark:border-slate-800 px-6 bg-white dark:bg-slate-900">
              <button
                onClick={() => setActiveTab('payloads')}
                className={`py-3 px-4 font-semibold text-xs border-b-2 flex items-center gap-2 transition-colors ${
                  activeTab === 'payloads'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Code2 className="w-4 h-4" />
                <span>Payloads (Req & Res)</span>
              </button>

              <button
                onClick={() => setActiveTab('headers')}
                className={`py-3 px-4 font-semibold text-xs border-b-2 flex items-center gap-2 transition-colors ${
                  activeTab === 'headers'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Cpu className="w-4 h-4" />
                <span>Gateway Trace & Headers</span>
              </button>

              <button
                onClick={() => setActiveTab('replay')}
                className={`py-3 px-4 font-semibold text-xs border-b-2 flex items-center gap-2 transition-colors ${
                  activeTab === 'replay'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Terminal className="w-4 h-4" />
                <span>Replay & cURL</span>
              </button>
            </div>

            {/* Tab Contents */}
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-5">
              {activeTab === 'payloads' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Request Payload */}
                  <div className="bg-slate-950 rounded-xl border border-slate-800 p-4 flex flex-col">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-3">
                      <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-sky-400" />
                        <span>Request Payload</span>
                      </span>
                      {selectedLog.request_payload && (
                        <button
                          onClick={() =>
                            copyToClipboard(
                              JSON.stringify(selectedLog.request_payload, null, 2),
                              'req-body'
                            )
                          }
                          className="text-slate-400 hover:text-white text-2xs inline-flex items-center gap-1 font-mono transition-colors"
                        >
                          {copiedKey === 'req-body' ? (
                            <Check className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                          <span>Copiar</span>
                        </button>
                      )}
                    </div>
                    {selectedLog.request_payload &&
                    Object.keys(selectedLog.request_payload).length > 0 ? (
                      <pre className="text-2xs font-mono text-emerald-400 whitespace-pre-wrap overflow-x-auto max-h-64 scrollbar-thin">
                        {JSON.stringify(selectedLog.request_payload, null, 2)}
                      </pre>
                    ) : (
                      <div className="py-12 text-center text-slate-500 text-xs">
                        Nenhum corpo de requisição registrado (ou requisição GET).
                      </div>
                    )}
                  </div>

                  {/* Response Payload */}
                  <div className="bg-slate-950 rounded-xl border border-slate-800 p-4 flex flex-col">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-3">
                      <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                        <span>Response Payload</span>
                      </span>
                      {selectedLog.response_payload && (
                        <button
                          onClick={() =>
                            copyToClipboard(
                              JSON.stringify(selectedLog.response_payload, null, 2),
                              'res-body'
                            )
                          }
                          className="text-slate-400 hover:text-white text-2xs inline-flex items-center gap-1 font-mono transition-colors"
                        >
                          {copiedKey === 'res-body' ? (
                            <Check className="w-3 h-3 text-emerald-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                          <span>Copiar</span>
                        </button>
                      )}
                    </div>
                    {selectedLog.response_payload &&
                    Object.keys(selectedLog.response_payload).length > 0 ? (
                      <pre className="text-2xs font-mono text-sky-400 whitespace-pre-wrap overflow-x-auto max-h-64 scrollbar-thin">
                        {JSON.stringify(selectedLog.response_payload, null, 2)}
                      </pre>
                    ) : (
                      <div className="py-12 text-center text-slate-500 text-xs">
                        Nenhum corpo de resposta capturado ou dados confidenciais foram omitidos por
                        política de retenção.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'headers' && (
                <div className="space-y-4">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-800 space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Kong Gateway & Metadados de Rede
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono">
                      <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <span className="text-slate-400 block text-2xs">X-Consumer-ID</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          {selectedLog.client_id || 'anonymous'}
                        </span>
                      </div>
                      <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <span className="text-slate-400 block text-2xs">X-Application-ID</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          {selectedLog.application_id || 'default-app'}
                        </span>
                      </div>
                      <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <span className="text-slate-400 block text-2xs">X-Gateway-Latency</span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {gatewayOverhead} ms
                        </span>
                      </div>
                      <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <span className="text-slate-400 block text-2xs">X-Upstream-Latency</span>
                        <span className="font-semibold text-sky-600 dark:text-sky-400">
                          {upstreamLatency} ms
                        </span>
                      </div>
                      <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <span className="text-slate-400 block text-2xs">Content-Type</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          application/json; charset=utf-8
                        </span>
                      </div>
                      <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800">
                        <span className="text-slate-400 block text-2xs">X-Gateway-Route</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          route_{selectedLog.endpoint.replace(/[^a-zA-Z0-9]/g, '_')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'replay' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                        Comando de Replay cURL
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Execute diretamente no seu terminal ou teste na ferramenta Sandbox Mocks
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(generateCurl(selectedLog), 'curl')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
                    >
                      {copiedKey === 'curl' ? (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Copiado!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copiar cURL</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                    <pre className="text-xs font-mono text-emerald-400 whitespace-pre-wrap overflow-x-auto">
                      {generateCurl(selectedLog)}
                    </pre>
                  </div>

                  <div className="p-4 bg-sky-50 dark:bg-sky-950/40 rounded-xl border border-sky-200/80 dark:border-sky-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Terminal className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                      <div>
                        <div className="text-xs font-bold text-sky-900 dark:text-sky-200">
                          Precisa simular ou mapear essa rota?
                        </div>
                        <div className="text-2xs text-sky-700 dark:text-sky-300">
                          Configure transformações ou respostas simuladas no Sandbox Mocks.
                        </div>
                      </div>
                    </div>
                    <Link
                      to="/sandbox-mocks"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 dark:text-sky-400 hover:underline"
                    >
                      <span>Abrir Sandbox</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3.5 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs text-slate-500 font-mono">
                Log Trace ID: <span className="font-semibold">{selectedLog.id}</span>
              </span>
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-100 rounded-xl text-xs font-semibold transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
